import type { AuditEntityType, AuditEventType, AuditTrailItem } from '@skewb-works/contracts';
import { format, parseISO } from 'date-fns';

function formatStatusChange(item: AuditTrailItem): string {
  const prev = item.previous_value;
  const next = item.new_value;

  const prevStr = typeof prev === 'string' ? prev : prev != null ? `status #${prev}` : '';
  const nextStr = typeof next === 'string' ? next : next != null ? `status #${next}` : '';

  if (prevStr) return `Status changed from '${prevStr}' to '${nextStr}'`;
  return `Status changed to '${nextStr}'`;
}

function extractUsername(value: unknown): string | null {
  if (value != null && typeof value === 'object' && 'username' in value) {
    return String((value as Record<string, unknown>).username);
  }
  if (value != null && typeof value === 'object' && 'userId' in value) {
    return `user #${(value as Record<string, unknown>).userId}`;
  }
  if (typeof value === 'string') return value;
  return null;
}

function formatAssignment(item: AuditTrailItem): string {
  const prevName = extractUsername(item.previous_value);
  const nextName = extractUsername(item.new_value);

  if (prevName && nextName) return `Reassigned from ${prevName} to ${nextName}`;
  if (nextName) return `Assigned to ${nextName}`;
  return 'Assignment updated';
}

function formatScheduleDate(isoString: string): string {
  try {
    return format(parseISO(isoString), 'd MMM HH:mm');
  } catch {
    return isoString;
  }
}

function formatSchedule(value: unknown): string {
  if (value == null || typeof value !== 'object') return '';
  const obj = value as Record<string, unknown>;
  const start = typeof obj.start === 'string' ? formatScheduleDate(obj.start) : '';
  const end = typeof obj.end === 'string' ? formatScheduleDate(obj.end) : '';
  if (start && end) return `${start}–${end}`;
  return start || end;
}

function formatAddressDiff(prev: unknown, next: unknown): string {
  if (prev == null && next != null) {
    const obj = next as Record<string, unknown>;
    return `Address set to '${obj.address ?? ''}'`;
  }
  if (prev == null || next == null) return 'Address updated';

  const prevObj = prev as Record<string, unknown>;
  const nextObj = next as Record<string, unknown>;

  const displayFields = ['address', 'town', 'county', 'pincode'] as const;
  const changes: string[] = [];

  for (const field of displayFields) {
    const pv = prevObj[field];
    const nv = nextObj[field];
    if (String(pv ?? '') !== String(nv ?? '')) {
      if (pv && nv) {
        changes.push(`${field}: '${pv}' → '${nv}'`);
      } else if (nv) {
        changes.push(`${field}: set to '${nv}'`);
      }
    }
  }

  const prevCoords = JSON.stringify(prevObj.coordinates ?? null);
  const nextCoords = JSON.stringify(nextObj.coordinates ?? null);
  if (prevCoords !== nextCoords) {
    changes.push('coordinates updated');
  }

  if (changes.length === 0) return 'Address updated';
  if (changes.length === 1) return `Address ${changes[0]}`;
  return `Address updated: ${changes.join(', ')}`;
}

function formatFieldUpdate(item: AuditTrailItem): string {
  const prev = item.previous_value;
  const next = item.new_value;

  switch (item.field_name) {
    case 'duration': {
      if (prev != null && next != null) return `Duration changed from ${prev}h to ${next}h`;
      if (next != null) return `Duration set to ${next}h`;
      return 'Duration updated';
    }
    case 'address':
      return formatAddressDiff(prev, next);
    case 'job_category_id': {
      const prevStr = prev != null ? String(prev) : null;
      const nextStr = next != null ? String(next) : null;
      if (prevStr && nextStr) return `Category changed from '${prevStr}' to '${nextStr}'`;
      if (nextStr) return `Category set to '${nextStr}'`;
      return 'Category updated';
    }
    case 'schedule': {
      const prevSchedule = formatSchedule(prev);
      const nextSchedule = formatSchedule(next);
      if (prevSchedule && nextSchedule)
        return `Schedule changed from ${prevSchedule} to ${nextSchedule}`;
      if (nextSchedule) return `Schedule set to ${nextSchedule}`;
      return 'Schedule updated';
    }
    default: {
      const fieldLabel = item.field_name ?? 'field';
      const prevStr =
        prev != null ? (typeof prev === 'object' ? JSON.stringify(prev) : String(prev)) : null;
      const nextStr =
        next != null ? (typeof next === 'object' ? JSON.stringify(next) : String(next)) : null;
      if (prevStr && nextStr)
        return `Field '${fieldLabel}' updated from '${prevStr}' to '${nextStr}'`;
      if (nextStr) return `Field '${fieldLabel}' set to '${nextStr}'`;
      return `Field '${fieldLabel}' updated`;
    }
  }
}

function formatDeletion(item: AuditTrailItem): string {
  if (item.field_name === 'schedule' && item.previous_value != null) {
    const prev = item.previous_value as Record<string, unknown>;
    const username = prev.username ?? (prev.userId ? `user #${prev.userId}` : null);
    const schedule = formatSchedule(prev);
    if (username && schedule) return `Schedule removed for ${username} (${schedule})`;
    if (username) return `Schedule removed for ${username}`;
    if (schedule) return `Schedule removed (${schedule})`;
  }
  return 'Deleted';
}

export function describeAuditEvent(item: AuditTrailItem): string {
  switch (item.event_type) {
    case 'status_change':
      return formatStatusChange(item);
    case 'assignment':
      return formatAssignment(item);
    case 'field_update':
      return formatFieldUpdate(item);
    case 'creation':
      return 'Created';
    case 'deletion':
      return formatDeletion(item);
    case 'completion':
      return 'Completed';
    case 'authority_override':
      if (item.reason) return `Authority override: ${item.reason}`;
      return 'Authority override applied';
    default:
      return item.event_type;
  }
}

const EVENT_TYPE_LABELS: Record<AuditEventType, string> = {
  status_change: 'Status Change',
  assignment: 'Assignment',
  completion: 'Completion',
  authority_override: 'Authority Override',
  field_update: 'Field Update',
  creation: 'Creation',
  deletion: 'Deletion',
};

export function formatEventType(eventType: AuditEventType): string {
  return EVENT_TYPE_LABELS[eventType] ?? eventType;
}

const ENTITY_TYPE_LABELS: Record<AuditEntityType, string> = {
  job: 'Job',
  work_order: 'Work Order',
  user: 'User',
};

export function formatEntityType(entityType: AuditEntityType): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}

export function formatTimestamp(isoString: string): string {
  try {
    return format(parseISO(isoString), 'd MMM yyyy, HH:mm');
  } catch {
    return isoString;
  }
}
